import { useState } from "react";
import api, { extractErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ImageIcon, Loader2 } from "lucide-react";

const CONFIRM = "MIGRATE MEDIA URLS";

export default function SuperMediaMigrate() {
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState("");

  const runPreview = async () => {
    setBusy("preview");
    setResult(null);
    try {
      const { data } = await api.get("/platform/media-url-migrate");
      setPreview(data);
      toast.success("Preview ready");
    } catch (e) {
      toast.error(extractErr(e));
    } finally {
      setBusy("");
    }
  };

  const runMigrate = async () => {
    const found = preview?.urls_found ?? 0;
    const ok = window.confirm(
      found
        ? `Replace ${found} stored URL(s) media.gro.codes → media.merastartup.in?\n\nR2 objects are not moved or deleted. Type-confirm next.`
        : "No matching URLs in the last preview. Run anyway?",
    );
    if (!ok) return;
    const phrase = window.prompt(`Type ${CONFIRM} to apply the migration:`);
    if (phrase !== CONFIRM) {
      toast.error("Confirmation phrase did not match. Nothing was changed.");
      return;
    }
    setBusy("migrate");
    try {
      const { data } = await api.post("/platform/media-url-migrate", { confirm: CONFIRM });
      setResult(data);
      setPreview(data);
      toast.success(`Updated ${data.urls_updated} URL(s)`);
    } catch (e) {
      toast.error(extractErr(e));
    } finally {
      setBusy("");
    }
  };

  const stats = result || preview;

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="font-display text-3xl font-bold">Media URL Migration</h1>
        <p className="text-slate-500 text-sm mt-1">
          Rewrite stored R2 public URLs from the old media hostname to the new one. Objects in the bucket are not changed.
        </p>
      </div>
      <Card className="bg-slate-900 border-slate-800 p-6 space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-indigo-500/20 border border-indigo-400/40 flex items-center justify-center flex-shrink-0">
            <ImageIcon className="w-4 h-4 text-indigo-300" />
          </div>
          <div className="text-sm space-y-1">
            <div><span className="text-slate-500">Old domain:</span> <span className="font-mono-nums text-slate-100">media.gro.codes</span></div>
            <div><span className="text-slate-500">New domain:</span> <span className="font-mono-nums text-slate-100">media.merastartup.in</span></div>
          </div>
        </div>
        <p className="text-xs text-slate-500">
          Only the exact hostname <span className="font-mono">media.gro.codes</span> is replaced.
          Paths stay identical. Unrelated <span className="font-mono">grocode.in</span> / <span className="font-mono">gro.codes</span> URLs are not touched.
        </p>
        <div className="flex gap-2">
          <Button onClick={runPreview} disabled={!!busy} data-testid="media-mig-preview" className="bg-slate-700 hover:bg-slate-600">
            {busy === "preview" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            Preview
          </Button>
          <Button onClick={runMigrate} disabled={!!busy} data-testid="media-mig-apply" className="bg-indigo-500 hover:bg-indigo-600">
            {busy === "migrate" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            Migrate
          </Button>
        </div>
      </Card>

      {stats && (
        <Card className="bg-slate-900 border-slate-800 p-6 space-y-2 text-sm" data-testid="media-mig-stats">
          <div className="font-semibold text-slate-100">{stats.apply ? "Migration result" : "Preview"}</div>
          <div className="text-slate-400">Documents scanned: <span className="text-slate-100 font-mono-nums">{Number(stats.documents_scanned).toLocaleString()}</span></div>
          <div className="text-slate-400">Matching URLs: <span className="text-slate-100 font-mono-nums">{Number(stats.urls_found).toLocaleString()}</span></div>
          <div className="text-slate-400">Documents with matches: <span className="text-slate-100 font-mono-nums">{Number(stats.documents_with_matches).toLocaleString()}</span></div>
          <div className="text-slate-400">URLs updated: <span className="text-slate-100 font-mono-nums">{Number(stats.urls_updated).toLocaleString()}</span></div>
          {stats.samples?.length > 0 && (
            <div className="mt-3 space-y-1">
              <div className="text-xs uppercase tracking-wider text-slate-500">Examples</div>
              {stats.samples.map((s, i) => (
                <div key={i} className="text-xs font-mono-nums text-slate-400 break-all">{s}</div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
